import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';

// Event names for GIS functionality
const GIS_EVENT_NAMES = {
  // Click-to-identify events
  CLICK_TO_IDENTIFY_FEATURE: 'click_to_identify_feature',

  // Layer events
  TOGGLE_SUPPLEMENTAL_LAYER: 'toggle_supplemental_layer',
  TOGGLE_BASEMAP_LAYER: 'toggle_basemap_layer',

  // Markup events
  TOGGLE_MARKUP_MODE: 'toggle_markup_mode',
  USE_COMPASS_NAVIGATION: 'use_compass_navigation',
};

// =============================================================================
// CLICK-TO-IDENTIFY TRACKING
// =============================================================================

/**
 * Tracks when a user clicks on a feature to identify it.
 * @param {Object} params - The parameters for tracking the feature identification
 * @param {string} params.layerName - The name of the layer containing the feature
 * @param {string} params.featureType - The type of feature clicked (e.g., 'polygon', 'point', 'line')
 * @param {number} params.featureCount - The number of features identified at the click location
 */
export const trackClickToIdentifyFeature = ({
  layerName,
  featureType,
  featureCount,
}: {
  layerName: string;
  featureType: string;
  featureCount: number;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.CLICK_TO_IDENTIFY_FEATURE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    entities: {
      layer_name: layerName,
      feature_type: featureType,
      feature_count: featureCount,
    },
  });
};

// =============================================================================
// LAYER TRACKING
// =============================================================================

/**
 * Tracks when a user toggles a supplemental layer visibility.
 * @param {Object} params - The parameters for tracking the supplemental layer toggle
 * @param {string} params.layerName - The name of the layer being toggled
 * @param {boolean} params.isVisible - Whether the layer is being shown (true) or hidden (false)
 * @param {string} params.layerCategory - The category of the supplemental layer (e.g., 'weather', 'traffic', 'infrastructure')
 */
export const trackToggleSupplementalLayer = ({
  layerName,
  isVisible,
  layerCategory,
}: {
  layerName: string;
  isVisible: boolean;
  layerCategory: string;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.TOGGLE_SUPPLEMENTAL_LAYER,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      layer_category: layerCategory,
      visibility_state: isVisible ? 'show' : 'hide',
    },
    entities: {
      layer_name: layerName,
    },
  });
};

/**
 * Tracks when a user toggles a basemap layer visibility.
 * @param {Object} params - The parameters for tracking the basemap layer toggle
 * @param {string} params.layerName - The name of the basemap being toggled
 * @param {boolean} params.isVisible - Whether the basemap is being shown (true) or hidden (false)
 * @param {string} params.basemapProvider - The provider of the basemap (e.g., 'google', 'esri', 'mapbox')
 */
export const trackToggleBasemapLayer = ({
  layerName,
  isVisible,
  basemapProvider,
}: {
  layerName: string;
  isVisible: boolean;
  basemapProvider: string;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.TOGGLE_BASEMAP_LAYER,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      basemap_provider: basemapProvider,
      visibility_state: isVisible ? 'show' : 'hide',
    },
    entities: {
      layer_name: layerName,
    },
  });
};

// =============================================================================
// MARKUP TRACKING
// =============================================================================

/**
 * Tracks when a user toggles markup mode on or off.
 * @param {Object} params - The parameters for tracking markup mode toggle
 * @param {boolean} params.isEnabled - Whether markup mode is being enabled (true) or disabled (false)
 */
export const trackToggleMarkupMode = ({
  isEnabled,
}: {
  isEnabled: boolean;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.TOGGLE_MARKUP_MODE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      markup_state: isEnabled ? 'enable' : 'disable',
    },
  });
};

/**
 * @param {Object} params - The parameters for tracking compass usage
 * @param {string} params.action - The compass action performed (rotate, reset, click_direction)
 */
export const trackUseCompassNavigation = ({ action }: { action: string }) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.USE_COMPASS_NAVIGATION,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      compass_action: action,
    },
  });
};
